import React from "react";

/**
 * Global Error Boundary — catches unhandled React errors and shows
 * a fallback UI instead of a white screen.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Always log to console (prod inclus) — pour debug avec F12
    console.error("[ErrorBoundary] Uncaught error:", error, errorInfo);

    // Auto-retry sur erreurs de chargement de chunk (lazy import network blip)
    // Common errors: "Loading chunk N failed", "ChunkLoadError", "Failed to fetch dynamically imported module"
    const errMsg = (error && (error.message || error.toString())) || "";
    if (
      errMsg.includes("Loading chunk") ||
      errMsg.includes("ChunkLoadError") ||
      errMsg.includes("dynamically imported module") ||
      errMsg.includes("Failed to fetch")
    ) {
      // Force hard reload pour récupérer les nouveaux chunks (probable post-deploy)
      console.warn("[ErrorBoundary] Chunk load error detected — auto-reloading...");
      // Petit délai pour laisser le state s'updater au cas où user voit
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
  }

  handleReload = () => {
    window.location.href = "/";
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-destructive"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Une erreur inattendue s'est produite
              </h1>
              <p className="text-muted-foreground">
                L'application a rencontré un problème. Essayez de rafraîchir la page.
              </p>
            </div>

            {/* Toujours afficher un brief en prod (pour signalement utilisateur) */}
            {this.state.error && (
              <details className="text-left bg-muted rounded-lg p-4 text-sm">
                <summary className="cursor-pointer font-medium text-foreground mb-2">
                  Détails techniques (à inclure dans un signalement)
                </summary>
                <pre className="whitespace-pre-wrap text-destructive text-xs overflow-auto max-h-48">
                  {this.state.error.toString()}
                  {process.env.NODE_ENV !== "production" && this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="px-4 py-2 rounded-md border border-border text-foreground hover:bg-accent transition-colors"
              >
                Réessayer
              </button>
              <button
                onClick={this.handleReload}
                className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Page d'accueil
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
